import React, { type ReactNode, useMemo } from 'react';

import { graphql, useFragment } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import { getProductAndIDFromARI } from '@atlassian/search-client/ari';
import { type SearchResultType as SearchClientResultType } from '@atlassian/search-client/common/types';
import { getJiraUrlWithAnalyticsQueryParams } from '@atlassian/search-common/analytics-query-params';
import { highlightMatchedText } from '@atlassian/search-common/highlight-matched-text';

import { ApplicationModes } from '../../../../../../common/constants/application-modes';
import { ProductKeys } from '../../../../../../common/constants/products';
import type { SearchResultProps } from '../../../../../../common/types';
import { DescriptionBody } from '../../../../../../common/ui/description-body';
import { LastModified } from '../../../../../../common/ui/last-modified';
import { getJiraEntityIcon } from '../../../../../../common/ui/product-entity-icon';
import { SearchResult } from '../../../../../../common/ui/search-result';
import { SearchResultType } from '../../../../../../common/ui/search-result-type';
import { BreadcrumbSocialSignalNext } from '../../../../../../common/ui/social-signals';
import type { AdvancedSearchResultInteractedMetadata } from '../../../../../../common/utils/events/advanced-search-result-interacted';
import {
	enrichExcerpt,
	getJiraSearchResultTypeMetadata,
	processExcerpt,
} from '../../../../../../common/utils/search-result-utils';
import { getSummarizedAdfContent } from '../../../../../../common/utils/summarized-adf-content';
import { useCommonAndPrivateAttributes } from '../../../../../../controllers/analytics';
import { useFireContextMenuButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-button-clicked';
import { useFireContextMenuCopyButtonClicked } from '../../../../../../controllers/events/use-fire-context-menu-copy-button-clicked';
import { useFireCopyLinkButtonClicked } from '../../../../../../controllers/events/use-fire-copy-link-button-clicked';
import { useFireResultClicked } from '../../../../../../controllers/events/use-fire-result-clicked';
import { useFireResultCtxMenuClicked } from '../../../../../../controllers/events/use-fire-result-context-menu-clicked';
import { useFireSummarizeInChatButtonClicked } from '../../../../../../controllers/events/use-fire-summarize-in-chat-button-clicked';

import type { searchJiraProject_AtlassianSearchPage$key } from './__generated__/searchJiraProject_AtlassianSearchPage.graphql';
import { SearchJiraProjectTableRow } from './table-row';

export type SearchJiraProjectProps = SearchResultProps<searchJiraProject_AtlassianSearchPage$key> & {
	/**
	 * When true, render the Jira project table-row variant instead of the
	 * standard SearchResult card. Controlled exclusively by the parent list
	 * renderer (SearchResultsListLiteRenderer) — leaf renderers MUST NOT
	 * independently read feature flags to flip into the table form, otherwise
	 * the table row leaks onto the "All" tab whenever the lite-mode bootstrap
	 * flag is on.
	 */
	shouldRenderTableResult?: boolean;
};

export const SearchJiraProject = ({
	node,
	position,
	cloudId,
	searchQuery,
	searchSessionId,
	selectedProduct,
	primaryProduct,
	appMode,
	mainContentWidth,
	shouldRenderTableResult = false,
}: SearchJiraProjectProps): React.JSX.Element => {
	const data = useFragment(
		graphql`
			fragment searchJiraProject_AtlassianSearchPage on SearchResultJiraProject
			@argumentDefinitions(includeJiraLiteModeFields: { type: "Boolean", defaultValue: false }) {
				__typename
				id
				title
				url
				type
				description
				projectKey
				lastModifiedDate
				iconUrl
				project {
					webUrl
					lead @include(if: $includeJiraLiteModeFields) {
						name
						picture
					}
					avatar @include(if: $includeJiraLiteModeFields) {
						small
					}
					projectStyle @include(if: $includeJiraLiteModeFields)
					projectType @include(if: $includeJiraLiteModeFields)
					category @include(if: $includeJiraLiteModeFields) {
						name
					}
				}
				socialSignal(tenantId: $tenantId) {
					signal
					timestamp
					user {
						accountId
						name
						picture
						... on AtlassianAccountUser {
							email
							extendedProfile {
								jobTitle
							}
						}
					}
				}
			}
		`,
		node,
	);

	const { commonAttributes } = useCommonAndPrivateAttributes();

	const metadata = useMemo(() => {
		const [product, , , contentId] = getProductAndIDFromARI(data.id);
		return {
			id: data.id,
			containerId: null,
			spaceId: null,
			contentType: data.type,
			entityType: data.type,
			contentId,
			product,
		} satisfies AdvancedSearchResultInteractedMetadata;
	}, [data.id, data.type]);
	const onResultClicked = useFireResultClicked({
		metadata,
		position,
	});
	const onResultCtxMenuClicked = useFireResultCtxMenuClicked({
		metadata,
		position,
	});

	const onCopyLinkButtonClicked = useFireCopyLinkButtonClicked('searchResultTitle');
	const onSummarizeInChatButtonClicked = useFireSummarizeInChatButtonClicked({
		location: 'searchResult',
		metadata,
		position,
	});
	const onContextMenuCopyButtonClicked = useFireContextMenuCopyButtonClicked();
	const onContextMenuButtonClicked = useFireContextMenuButtonClicked();

	const url = useMemo(() => {
		const initialResultUrl = data.project?.webUrl || data.url;
		return getJiraUrlWithAnalyticsQueryParams({
			url: initialResultUrl,
			searchSessionId,
			resultId: data.id,
		});
	}, [data.project?.webUrl, data.url, data.id, searchSessionId]);

	const enrichedTitle = useMemo(() => {
		const prefix = data.projectKey;
		const { highlightedTitle } = highlightMatchedText(data.title, searchQuery.split(' '));

		if (prefix && !(highlightedTitle?.startsWith(prefix) || data.title.startsWith(prefix))) {
			return !highlightedTitle
				? prefix.concat(': ', data.title)
				: enrichExcerpt(prefix.concat(': ', highlightedTitle));
		}

		return !highlightedTitle ? data.title : enrichExcerpt(highlightedTitle);
	}, [data.title, data.projectKey, searchQuery]);

	const enrichedDescription = useMemo(() => {
		const reducedDescription = getSummarizedAdfContent(data.description, searchQuery);
		const { highlightedTitle } = highlightMatchedText(reducedDescription, searchQuery.split(' '));
		if (!highlightedTitle) {
			return reducedDescription;
		}
		return processExcerpt(highlightedTitle);
	}, [data.description, searchQuery]);

	const metadataParts: [string, ReactNode][] = useMemo(() => {
		const parts = [
			[
				'type',
				<SearchResultType
					type={getJiraSearchResultTypeMetadata(data.type as SearchClientResultType)}
				/>,
			],
			[
				'lastModified',
				data.lastModifiedDate ? <LastModified lastModified={data.lastModifiedDate} /> : null,
			],
		];
		if (
			appMode === ApplicationModes.Rovo &&
			data.socialSignal?.user?.email &&
			fg('search-plex-jira-social-signal')
		) {
			const { user, signal, timestamp } = data.socialSignal;
			parts.push([
				'social-signal',
				<BreadcrumbSocialSignalNext
					userAccountId={user.accountId}
					userName={user.name}
					userEmail={data.socialSignal?.user?.email}
					userPicture={user.picture}
					userJobTitle={user.extendedProfile?.jobTitle}
					signal={signal}
					timestamp={timestamp}
					commonAttributes={commonAttributes}
				/>,
			]);
		}
		return parts.filter(Boolean) as [string, ReactNode][];
	}, [data.socialSignal, data.type, data.lastModifiedDate, appMode, commonAttributes]);

	const icon = getJiraEntityIcon({
		iconUrl: data.iconUrl,
		type: data.type,
		isIssue: false,
		size: 'medium',
	});

	if (shouldRenderTableResult) {
		return (
			<SearchJiraProjectTableRow
				iconUrl={data.project?.avatar?.small ?? ''}
				type={data.type}
				projectStyle={data.project?.projectStyle ?? ''}
				projectType={data.project?.projectType ?? ''}
				projectKey={data.projectKey}
				projectUrl={url}
				title={enrichedTitle}
				lead={
					data.project?.lead
						? { name: data.project.lead.name, picture: data.project.lead.picture }
						: null
				}
				category={data.project?.category?.name ?? ''}
				// TODO: properly wire up analytics
				onClick={() => onResultClicked({})}
			/>
		);
	}

	return (
		<SearchResult
			{...data}
			product={ProductKeys.Jira}
			productSelected={!!selectedProduct}
			primaryProduct={primaryProduct}
			onResultClicked={onResultClicked}
			onResultContextMenu={onResultCtxMenuClicked}
			onCopyLinkButtonClicked={onCopyLinkButtonClicked}
			onSummarizeInChatButtonClicked={onSummarizeInChatButtonClicked}
			onContextMenuCopyButtonClicked={onContextMenuCopyButtonClicked}
			onContextMenuButtonClicked={onContextMenuButtonClicked}
			url={url}
			icon={icon}
			title={enrichedTitle}
			headerMetadataParts={metadataParts}
			description={<DescriptionBody description={enrichedDescription} />}
			cloudId={cloudId}
			position={position}
			partial={data}
			appMode={appMode}
			mainContentWidth={mainContentWidth}
		/>
	);
};

export const isSearchJiraProject = (
	node?: any,
): node is searchJiraProject_AtlassianSearchPage$key => {
	return node?.__typename === 'SearchResultJiraProject';
};
